<template>
  <component
    :is="hasTooltip ? MsTooltip : 'div'"
    placement="left"
    :hide-after="0"
    :show-after="400"
    :visible-arrow="true"
  >
    <template #content>
      <div
        class="tooltip-content"
        v-html="$commonFn.encodeHtml(notify?.Message)"
      />
    </template>
    <div
      v-if="!isEmpty"
      :class="containerClass"
      @click.stop="onClickNotifyItem"
    >
      <div class="content-left">
        <img
          class="image"
          :src="image"
        >
      </div>
      <div
        class="content-right"
        v-if="notify"
      >
        <div
          ref="message"
          class="notify-text"
          v-html="$commonFn.encodeHtml(notify?.Message)"
        />
        <div class="time">
          {{ timeFormat }}
        </div>
      </div>
    </div>
    <div
      v-else
      :class="containerClass"
    >
      <div class="content-left">
        <div class="image empty-item" />
      </div>
      <div class="content-right">
        <div class="empty-item text-loading" />
        <div class="empty-item text-loading" />
        <div class="empty-item text-loading" />
      </div>
    </div>
  </component>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import MsTooltip from '@/components/tooltip'
import { removeDangerousString } from '@/common/common-fn'
import { formatText } from '@/utils/text'
import { isUuid, emptyUuid } from '@/utils/uuid'
import BaseList from '@/views/base/base-list'
import { ActionTypeFireBase } from '@/common/constant'

const platformNotifyPopup = () =>
  require('@/views/pages/common/notify/platform-notify-popup.vue')
const exerciseSubmitPopup = () =>
  import('@/views/pages/student/exercise/popup/exercise-submited-popup.vue')

const OPTION_ID = 'NotifiDontPractiveExercise'

export default {
  emits: ['onClick', 'showPopupJoinClassFail', 'showPopupJoinNewClass'],
  extends: BaseList,
  components: {
    MsTooltip
  },

  props: {
    notify: {
      type: Object,
      default: () => {},
      validator: (value) => {
        return value || {}
      }
    },
    isEmpty: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      MsTooltip: MsTooltip,
      hasTooltip: false,
      platformNotifyPopup: 'platformNotifyPopup',
      exerciseSubmitPopup: 'exerciseSubmitPopup'
    }
  },

  computed: {
    ...mapState('student-exercise', ['studentExercise']),
    /**
     * @author PVThang
     * @description format thời gian
     */
    timeFormat () {
      const me = this
      const timeNotify = new Date(me.notify?.Time)
      const now = new Date()
      const totalTime = now - timeNotify

      if (totalTime < 0) {
        const test = {
          year: timeNotify.getFullYear(),
          month: formatText(timeNotify.getMonth() + 1, 2, '0'),
          date: formatText(timeNotify.getDate() + 1, 2, '0'),
          day: me.$t(`DateFormat.DayNames[${timeNotify.getDay() + 7}]`),
          hour: formatText(timeNotify.getHours(), 2, '0'),
          minute: formatText(timeNotify.getMinutes(), 2, '0')
        }
        return me.$t('Notify.TimeTemplate.SameYear', test)
      }

      // Nhỏ hơn 1 phút
      const minutes = Math.floor(totalTime / (1000 * 60))
      if (minutes === 0) {
        return me.$t('Notify.TimeTemplate.JustNow')
      }

      // Nhỏ hơn 1 tiếng
      if (minutes < 60) {
        return me.$t('Notify.TimeTemplate.MinutesAgo', { minutes: minutes })
      }

      // cùng ngày
      const hours = Math.floor(totalTime / (1000 * 60 * 60))
      if (minutes < 60 * 24 && now.getDate() === timeNotify.getDate()) {
        return me.$t('Notify.TimeTemplate.HoursAgo', { hours: hours })
      }

      const params = {
        year: timeNotify.getFullYear(),
        month: formatText(timeNotify.getMonth() + 1, 2, '0'),
        date: formatText(timeNotify.getDate() + 1, 2, '0'),
        day: me.$t(`DateFormat.DayNames[${timeNotify.getDay() + 7}]`),
        hour: formatText(timeNotify.getHours(), 2, '0'),
        minute: formatText(timeNotify.getMinutes(), 2, '0')
      }

      // hôm qua
      const timeOfYestrday = now - 1000 * 60 * 60 * 24
      if (
        Math.abs(timeOfYestrday - timeNotify) < 1000 * 60 * 60 * 24 &&
        new Date(timeOfYestrday).getDate() === timeNotify.getDate()
      ) {
        return me.$t('Notify.TimeTemplate.Yesterday', params)
      }

      // nhỏ hơn 1 tuần
      if (totalTime < 1000 * 60 * 60 * 24 * 7) {
        return me.$t('Notify.TimeTemplate.SameWeek', params)
      }

      // cùng năm
      if (timeNotify.getFullYear() === now.getFullYear()) {
        return me.$t('Notify.TimeTemplate.SameYear', params)
      }

      return me.$t('Notify.TimeTemplate.DifferentYear', params)
    },

    /**
     * các class của container
     */
    containerClass () {
      const me = this

      return {
        'notify-item': true,
        'un-read': !me.isEmpty && !me.notify?.IsRead,
        'is-empty': me.isEmpty
      }
    },

    /**
     * Ảnh hển thị trên thông báo
     */
    image () {
      const me = this
      const notifyTypes = me.$enumeration.NotifyType
      const image = me.notify?.Image || {}
      const imageWidth = 100

      if (
        me.notify?.Type === notifyTypes.PlatformNotify ||
        me.notify?.Type === notifyTypes.ISMACNotify
      ) {
        return require('@/assets/icons/svg/ic_speaker.svg')
      }

      if (
        [
          notifyTypes.StudentJoinClassroom,
          notifyTypes.StudentRequestJoinClassroom,
          notifyTypes.TeacherApproveStudent,
          notifyTypes.TeacherKickStudent,
          notifyTypes.StudentIsAddedToClass,
          notifyTypes.StudentLeaveClassroom,
          notifyTypes.TeacherStopFollowClass,
          notifyTypes.TeacherDeleteClass
        ].includes(me.notify?.Type)
      ) {
        const isImageUpload = isUuid(image.FileID) && image.FileID !== emptyUuid
        const res = me.$commonFn.getClassroomCoverImage(
          isImageUpload ? image.FileID : image.FileName,
          imageWidth,
          image.OwnerID
        )
        return isImageUpload ? res : res?.src
      }

      return me.$commonFn.getExerciseOrClassImage(image)
    }
  },

  setup () {
    return {
      removeDangerousString
    }
  },

  methods: {
    ...mapActions('notify', ['readNotify', 'getContentForISMACNotify']),

    ...mapActions('student-exercise', [
      'getStudentExerciseByID',
      'logELK',
      'updateSubmitExerciseSaveDraft'
    ]),

    ...mapActions('user-option', [
      'getUserOption'
    ]),

    /**
     * @author PVThang
     * @description xử lý khi click vào thông báo
     */
    async onClickNotifyItem () {
      const me = this
      let path = ''
      const notifyTypes = me.$enumeration.NotifyType
      const encodeHtml = me.$commonFn.encodeHtml
      const data = me.notify?.Data || {}

      // Đánh dấu là đã đọc
      if (
        !me.notify.IsRead &&
        me.notify.Type !== notifyTypes?.ISMACNotify &&
        me.notify.Type !== notifyTypes?.PlatformNotify
      ) {
        me.readNotify(me.notify.KeyGroup)
      }
      // close popover
      me.$emit('onClick', me.notify)
      // redirect:
      path = me.getRoute()
      path && me.$router.push(path)
      // show popup
      switch (me.notify.Type) {
        case notifyTypes?.TeacherRefuseStudent:
          me.showMessageBox(
            me.$t('Notify.MessageTeaherRefuseStudent', {
              ClassName: encodeHtml(data.ClassName),
              TeacherName: encodeHtml(data.TeacherName)
            })
          )
          break
        case notifyTypes?.TeacherKickStudent:
          me.showMessageBox(
            me.$t('Notify.MessageTeacherKickStudent', {
              ClassName: encodeHtml(data.ClassName),
              TeacherName: encodeHtml(data.TeacherName)
            })
          )
          break
        case notifyTypes?.TeacherStopFollowClass:
          me.showMessageBox(
            me.$t('Notify.TeacherStopFollowClass', {
              ClassName: encodeHtml(data.ClassName),
              TeacherName: encodeHtml(data.TeacherName)
            })
          )
          break
        case notifyTypes?.TeacherDeleteClass:
          me.showMessageBox(
            me.$t('Notify.TeacherDeleteClass', {
              ClassName: encodeHtml(data.ClassName),
              TeacherName: encodeHtml(data.TeacherName)
            })
          )
          break
        case notifyTypes?.TeacherDeleteExamPeriod:
          me.showMessageBox(
            me.$t('Notify.TeacherDeleteExamPeriod', {
              TeacherName: encodeHtml(data.TeacherName),
              ExamName: encodeHtml(data.ExamName)
            })
          )
          break
        case notifyTypes?.TeacherDeleteExamResult:
          me.showMessageBox(
            me.$t('Notify.TeacherDeleteExamResult', {
              TeacherName: encodeHtml(data.TeacherName),
              ExamName: encodeHtml(data.ExamName)
            })
          )
          break
        case notifyTypes?.ISMACNotify:
        case notifyTypes?.PlatformNotify:
          if (!me.notify.Content && me.notify.NotificationID) {
            me.getContentForISMACNotify({
              notificationId: me.notify.NotificationID
            }).then((res) => {
              me.showDetail(me.platformNotifyPopup, { notify: res })
            })
          } else {
            me.showDetail(me.platformNotifyPopup, { notify: me.notify })
          }
          break
        case notifyTypes?.TeacherGiveExerciseHasDeadline:
        case notifyTypes?.TeacherGiveExerciseIndefinite:
        case notifyTypes?.TeacherExtendExercise:
        case notifyTypes?.TeacherAddReferenceExercise:
        case notifyTypes?.TeacherChangeDeadlineAndReferenceExercise:
          me.logFireBaseFull(
            ActionTypeFireBase.Update,
            'Learning',
            'Learning_DoHomeWork'
          )
          if (data.ExerciseType === me.$enumeration.ExerciseType.GameGalactic.toString()) {
            me.$router.push(`/student/exercise/game/galactic?exerciseID=${data.ExerciseID}`)
          } else {
            me.practiveExercise(data)
          }
          break
      }
    },

    /**
     * @author PVThang
     * @description Show popup thông báo cho học sinh khi bị từ chối/xóa khỏi lớp
     */
    async showMessageBox (message) {
      const me = this

      me.$message.show({
        message: message,
        confirmButtonText: me.$t('Button.Agree'),
        showCancelButton: false,
        showIgnoreButton: false,
        dangerouslyUseHTMLString: true,
        callback: () => {}
      })
    },

    /**
     * @author PVThang
     * @description Lấy ra route tương ứng với notify type
     */
    getRoute () {
      const me = this
      const notifyTypes = me.$enumeration.NotifyType
      const data = me.notify?.Data || {}
      const studentClassroom = '/student/classroom'
      const studentExercise = '/student/exercise'
      // const gameGalactic = `/student/classroom/game/galactic?exerciseID=${data.ExerciseID}`
      // const studentExerciseDetail = data.ExerciseType === me.$enumeration.ExerciseType.GameGalactic.toString()
      //   ? gameGalactic
      //   : `${studentClassroom}/${data.ClassCode}/exercise/detail?Id=${data.ExerciseID}&start=true`
      const teacherClassroomDetail = `/teacher/classroom/${data.ClassCode}`
      const studentExamRoute = '/student/student-exam'
      const commentHocMai = `/student/storage-exercise/detail-outline/hoc-mai/${data.TopicID}/${data.ThematicID}/${data.CourseID}/${data.LessonID}?id=${data.HocMaiCourseID}`
      const templateRoute = {
        StudentJoinClassroom: `${teacherClassroomDetail}/student`, // Học sinh tham gia lớp không cần phê duyệt
        StudentRequestJoinClassroom: `${teacherClassroomDetail}/student`, // Học sinh yêu cầu tham gia lớp
        TeacherApproveStudent: studentClassroom, // Giáo viên chấp nhận học sinh vào lớp
        TeacherRefuseStudent: studentClassroom, // Giáo viên từ chối học sinh vào lớp
        TeacherKickStudent: studentClassroom, // Giáo viên xóa học sinh khỏi lớp
        StudentIsAddedToClass: studentClassroom, // học sinh được thêm vào lớp
        StudentLeaveClassroom: `${teacherClassroomDetail}/student`, // Học sinh rời lớp
        // TeacherGiveExerciseHasDeadline: studentExerciseDetail, // Giáo viên giao bài tập có hạn nộp cho học sinh
        // TeacherGiveExerciseIndefinite: studentExerciseDetail, // Giáo viên giao bài tập không hạn nộp cho học sinh
        // TeacherExtendExercise: studentExerciseDetail, // Giáo viên gia hạn bài tập
        // TeacherAddReferenceExercise: studentExerciseDetail, // Giáo viên thêm tài liệu tham khảo
        // TeacherChangeDeadlineAndReferenceExercise: studentExerciseDetail, // Giáo viên Thêm tài liệu tham khảo và gia hạn bài tập
        TeacherRecallExercise: `${studentClassroom}/${data.ClassCode}/exercise`, // Giáo viên thu hồi bái tập
        TeacherCorrectExercise: `${studentExercise}/repair?id=${
          data.StudentExerciseID || data.ExerciseID
        }`, // Giáo viên chữa bài
        StudentSubmitExercise: `${teacherClassroomDetail}/exercise/${data.ExerciseID}/default`, // Học sinh nộp bài
        StudentQuestionExericse: '', // Phụ huynh/Học sinh hỏi về bài tập
        TeacherAnswer: '', // Giáo viên trả lời câu hỏi
        ExerciseExpirationSoon: '', // Bài tập sắp hết hạn
        ExerciseExpired: '', // Bài tập đã hết hạn
        TeacherStopFollowClass: '', // Giáo viên ngừng theo dõi lớp
        TeacherDeleteClass: '', // Giáo viên xóa lớp
        TeacherAddExamPeriod: studentExamRoute, // Giáo viên tạo bài kiểm tra
        TeacherDeleteExamPeriod: studentExamRoute, // Giáo viên xóa bài kiểm tra
        TeacherOpenExamPeriod: studentExamRoute, // Giáo viên mở bài kiểm tra
        TeacherReturnExamResult: `${studentExamRoute}/result/${data.StudentExamID}`, // Giáo viên trả kết quả kiểm tra cho học sinh
        TeacherDeleteExamResult: '', // Giáo viên hủy kết quả kiểm tra
        TeacherSendExamStore: `/teacher/school-exam-period/subject?schoolExamPeriodDetailId=${data.ExamPeriodID}&fromNotifyExamStore=true`, // Giáo viên gửi đề kiểm tra cho hội đồng
        TeacherSendExamResult: `/teacher/school-exam-period/subject?schoolExamPeriodDetailId=${
          data.ExamPeriodID
        }&isFromOverView=${false}&classIdOverview=`, // Giáo viên gửi kết quả kiểm tra cho hội đồng
        CouncilAssignToCompose: '/teacher/school-exam-period/assignment', // Hội đồng phân công soạn đề
        CouncilAssignToMark: '/teacher/school-exam-period/assignment', // Hội đồng phân công chấm bài
        StudentSubmitExam: `/teacher/exam-period/${data.ExamPeriodID}?type=${me.$enumeration.TypeExam.ExamTeacher}`, // Học sinh nộp bài kiểm tra
        TeacherAddStudentPackage: '/student/practice', // Giáo viên cấp thẻ khóa học cho sinh
        // Newsfeed
        ReplyComment: commentHocMai,
        UserLikeComment: commentHocMai
      }
      const typeName = Object.keys(notifyTypes)?.find((key) => {
        return notifyTypes[key] === me.notify?.Type
      })

      return templateRoute[typeName] || ''
    },

    /**
     * @author PVThang
     * @description Lấy ảnh theo ID
     */
    getImageByFileID (file) {
      const me = this

      if (!file) {
        return
      }

      return me.$commonFn.buildPreviewFileUrl({
        fileId: file.FileID,
        storageType: file.StorageType,
        ownerId: file.OwnerID,
        isTemp: false,
        isPreview: true
      })
    },

    /**
     * @description Lấy UI popup
     * @author lhlong
     */
    async getOtherPopup (componentName) {
      const me = this

      switch (componentName) {
        case me.platformNotifyPopup:
          return await platformNotifyPopup()
        case me.exerciseSubmitPopup:
          return await exerciseSubmitPopup()
        default:
          break
      }
    },

    // Xử lý sự kiện làm bài
    async practiveExercise (data) {
      const me = this
      if (data?.ExerciseID) {
        await me.getStudentExerciseByID(data.ExerciseID)
        var param = {
          SubmitExerciseID: '',
          IsReplay: false
        }
        //  cập nhật Id bài lưu nháp và trạng thái làm tiếp theo
        me.updateSubmitExerciseSaveDraft(param)

        //  lấy ra những lần làm bài trước đó
        var listSumbitExercise = me.studentExercise.SubmitDetails

        // TH lần đầu làm bài
        if (listSumbitExercise.length === 0) {
          me.redirectLinkExercise()
        } else {
        // TH đã có bài nộp
          var checkFinish = false
          var checkDraft = false
          // Kiểm tra các bản ghi đã nộp có bản ghi đã nộp và bản ghi nháp hay không
          listSumbitExercise.forEach((element) => {
            if (element.Status === me.$enumeration.ExerciseStatus.Complete) {
              checkFinish = true
            } else {
              checkDraft = true
              param.SubmitExerciseID = element.SubmitExerciseID
              param.IsReplay = false
            }
          })
          // TH đã có bài nộp nhưng không có bài lưu nháp
          if (checkFinish === true && checkDraft === false) {
            me.getUserOption(OPTION_ID).then((res) => {
              if (res) {
                if (me.userOption && me.userOption[OPTION_ID]) {
                  me.redirectLinkExercise()
                } else {
                  me.showDetail(me.exerciseSubmitPopup)
                }
              } else {
                me.showDetail(me.exerciseSubmitPopup)
              }
            })
          } else if (checkDraft === true) {
          // TH đã có bài nộp và có bài nháp
            me.updateSubmitExerciseSaveDraft(param)
            me.redirectLinkExercise(checkDraft)
          }
        }
      } else {
        me.logELK({
          path: me.$route.path,
          query: me.$route.query,
          params: me.$route.params,
          data: data,
          func: 'NotifyItem' // Fix tạm để log elk
        })
      }
    },

    // Điều hướng đến màn làm bài
    redirectLinkExercise (exerciseDraft) {
      const me = this
      const typeTab = me.$enumeration.StudentTabView.Exercises
      if (me.studentExercise.Type === me.$enumeration.ExerciseType.Compose) {
        exerciseDraft
          ? me.$router.push({
            path: '/student/exercise/theme',
            query: {
              type: typeTab,
              id: me.$route.query.Id,
              classCode: me.studentExercise.ClassCode
            }
          })
          : me.$router.push({
            path: '/student/exercise/theme',
            query: {
              type: typeTab,
              id: me.$route.query.Id,
              classCode: me.studentExercise.ClassCode
            }
          })
      } else if (me.studentExercise.Type === me.$enumeration.ExerciseType.Upload) {
        me.$router.push({
          path: '/student/exercise/answer',
          query: {
            type: typeTab,
            id: me.$route.query.Id,
            classCode: me.studentExercise.ClassCode
          }
        })
      } else {
        me.$router.push({
          path: '/student/exercise/answer',
          query: {
            type: typeTab
          }
        })
      }
    }
  },

  mounted () {
    const me = this
    const eleContentNotify = me.$refs.message

    me.hasTooltip =
      eleContentNotify?.scrollHeight > eleContentNotify?.clientHeight
  }
}
</script>

<style lang="scss" scoped>
$content-left-width: 76px;

.notify-item {
  width: 100%;
  height: 100px;
  padding: 12px 24px;
  @include overflow(hidden, hidden);
  display: grid;
  grid-gap: 16px;
  grid-template-columns: $content-left-width auto;
  cursor: pointer;
  text-align: left;
  outline: none !important;

  &.un-read {
    background-color: rgba($color: $color-accent, $alpha: 0.1);
  }

  .content-left {
    .image {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 10px;
    }

    .image-empty {
      width: 100%;
      height: 100%;
      background-color: $color-neutral-light-1;
      border-radius: 10px;
    }
  }

  .content-right {
    width: 100%;

    .notify-text {
      @include webkit-box(3);
      @include body();
      word-break: break-word;
      color: $color-neutral-light-5;
    }

    .time {
      padding-top: 4px;
      @include caption();
      color: $color-neutral-light-3;
    }
  }

  &.is-empty {
    .empty-item {
      background-color: $color-neutral-light-1;
      animation: loading 3s infinite;

      @keyframes loading {
        0% {
          background-color: $color-neutral-light-2;
        }
        50% {
          background-color: rgba($color: $color-neutral-light-3, $alpha: 0.75);
        }
        100% {
          background-color: $color-neutral-light-2;
        }
      }
    }

    .content-right {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: space-around;

      .text-loading {
        width: 100%;
        height: 8px;
        border-radius: 3px;
      }
    }
  }
}

.tooltip-content {
  width: 400px;
}
</style>
